import { PartyPicker } from "@/components/PartyPicker";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PartyService } from "@/gen/omnios/v1/party_connect";
import { TokenService } from "@/gen/omnios/v1/token_connect";
import { transport } from "@/lib/grpc";
import { usePortalDetail } from "@/routes/portals.$id";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Check, Copy, Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/portals/$id/keys")({
	component: KeysTab,
});

const client = createClient(TokenService, transport);
const partyClient = createClient(PartyService, transport);

// ── Scope definitions ──

const AVAILABLE_SCOPES = [
	{ value: "products:read", labelKey: "scope_products_read" },
	{ value: "products:write", labelKey: "scope_products_write" },
	{ value: "categories:read", labelKey: "scope_categories_read" },
	{ value: "properties:read", labelKey: "scope_properties_read" },
	{ value: "keys:mint", labelKey: "scope_keys_mint" },
] as const;

function PartyName({ partyId }: { partyId: string }) {
	const { data } = useQuery({
		queryKey: ["party", partyId],
		queryFn: () => partyClient.getParty({ id: partyId }),
		enabled: !!partyId,
	})
	const name = data?.displayName || data?.name || "...";
	return (
		<Badge variant="secondary" className="text-[10px] px-1.5 py-0">
			{name}
		</Badge>
	)
}

// ── Date helpers ──

function formatDate(ts: { seconds: bigint } | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function relativeTime(
	ts: { seconds: bigint } | undefined,
	t: (key: string, opts?: any) => string,
): string {
	if (!ts) return t("never");
	const diff = Date.now() - Number(ts.seconds) * 1000;
	if (diff < 60_000) return t("just_now");
	if (diff < 3_600_000)
		return t("minutes_ago", { count: Math.floor(diff / 60_000) });
	if (diff < 86_400_000)
		return t("hours_ago", { count: Math.floor(diff / 3_600_000) });
	return t("days_ago", { count: Math.floor(diff / 86_400_000) });
}

function isExpired(ts: { seconds: bigint } | undefined): boolean {
	if (!ts) return false;
	return Number(ts.seconds) * 1000 < Date.now();
}

// ── Component ──

function KeysTab() {
	const { t } = useTranslation("portals");
	const { id: portalInstanceId } = useParams({ from: "/portals/$id/keys" });
	const ctx = usePortalDetail();

	const [showCreate, setShowCreate] = useState(false);
	const [revokeId, setRevokeId] = useState<string | null>(null);
	const [plaintext, setPlaintext] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	// Form
	const [name, setName] = useState("");
	const [origins, setOrigins] = useState("");
	const [rateLimit, setRateLimit] = useState("120");
	const [expiryDays, setExpiryDays] = useState("365");
	const [scopes, setScopes] = useState<string[]>([]);
	const [partyId, setPartyId] = useState("");
	const [creating, setCreating] = useState(false);

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}

	const handleCreate = async () => {
		if (!name.trim()) return;
		setCreating(true);
		try {
			const res = await client.createSearchKey({
				name: name.trim(),
				allowedOrigins: origins
					.split("\n")
					.map((s) => s.trim())
					.filter(Boolean),
				rateLimitRpm: Number.parseInt(rateLimit, 10) || 0,
				expiresInDays: Number.parseInt(expiryDays, 10) || 365,
				portalInstanceId,
				scopes,
				partyId,
			})
			setPlaintext(res.plaintext);
			setShowCreate(false);
			setName("");
			setOrigins("");
			setRateLimit("120");
			setExpiryDays("365");
			setScopes([]);
			setPartyId("");
			ctx.reload();
		} catch (e: unknown) {
			toast.error(
				t("error_prefix", {
					message: e instanceof Error ? e.message : "unknown",
				}),
			)
		} finally {
			setCreating(false);
		}
	}

	const handleRevoke = async () => {
		if (!revokeId) return;
		try {
			await client.revokeSearchKey({ keyId: revokeId });
			setRevokeId(null);
			ctx.reload();
		} catch (e: unknown) {
			toast.error(
				t("error_prefix", {
					message: e instanceof Error ? e.message : "unknown",
				}),
			)
		}
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">{t("api_keys")}</h2>
					<p className="text-sm text-muted-foreground">{t("api_keys_desc")}</p>
				</div>
				<Button
					onClick={() => setShowCreate(true)}
					size="sm"
					className="shrink-0"
				>
					<Plus className="h-4 w-4 mr-1.5" />
					{t("create")}
				</Button>
			</div>

			{/* List */}
			{ctx.loading ? (
				<div className="flex justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : ctx.keys.length === 0 ? (
				<Card className="px-4 py-8 text-center">
					<p className="text-sm text-muted-foreground">{t("no_keys")}</p>
				</Card>
			) : (
				<div className="space-y-2">
					{ctx.keys.map((key) => {
						const revoked = !!key.revokedAt;
						const expired = isExpired(key.expiresAt);
						return (
							<div
								key={key.id}
								className={`flex items-start justify-between rounded-lg border px-4 py-3 ${revoked || expired ? "opacity-50" : ""}`}
							>
								<div className="min-w-0 space-y-1">
									<div className="flex items-center gap-2">
										<span className="font-medium text-sm">{key.name}</span>
										{revoked ? (
											<Badge
												variant="destructive"
												className="text-[10px] px-1.5 py-0"
											>
												{t("revoked")}
											</Badge>
										) : expired ? (
											<Badge
												variant="secondary"
												className="text-[10px] px-1.5 py-0"
											>
												{t("expired")}
											</Badge>
										) : (
											<Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
												{t("active")}
											</Badge>
										)}
									</div>
									<div className="text-xs text-muted-foreground font-mono">
										{key.tokenPrefix}...
									</div>
									<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
										<span>
											{t("created_at")} {formatDate(key.createdAt)}
										</span>
										<span>
											{t("expires_at")} {formatDate(key.expiresAt)}
										</span>
										<span>
											{t("last_request")} {relativeTime(key.lastUsedAt, t)}
										</span>
									</div>
									{key.allowedOrigins.length > 0 && (
										<div className="flex gap-1 flex-wrap pt-0.5">
											{key.allowedOrigins.map((o) => (
												<span
													key={o}
													className="text-[10px] font-mono border rounded px-1.5 py-0 text-muted-foreground"
												>
													{o}
												</span>
											))}
										</div>
									)}
									{key.rateLimitRpm > 0 && (
										<div className="text-[11px] text-muted-foreground">
											{key.rateLimitRpm} {t("rate_limit_unit")}
										</div>
									)}
									<div className="flex gap-1 flex-wrap pt-0.5">
										{key.scopes.length > 0 ? (
											key.scopes.map((s) => (
												<Badge
													key={s}
													variant="outline"
													className="text-[10px] px-1.5 py-0"
												>
													{s}
												</Badge>
											))
										) : (
											<span className="text-[10px] text-muted-foreground">
												{t("scopes_all_read")}
											</span>
										)}
										{key.partyId && <PartyName partyId={key.partyId} />}
									</div>
								</div>
								{!revoked && (
									<Button
										variant="ghost"
										size="sm"
										className="text-muted-foreground hover:text-destructive shrink-0 h-8 w-8 p-0"
										onClick={() => setRevokeId(key.id)}
										aria-label={t("revoke")}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								)}
							</div>
						)
					})}
				</div>
			)}

			{/* ── Create dialog ──────────────────────── */}
			<Dialog open={showCreate} onOpenChange={setShowCreate}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("new_key")}</DialogTitle>
						<DialogDescription>{t("new_key_desc")}</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-1">
						<Field label={t("name_label")}>
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder={t("key_name_placeholder")}
								className="h-8 text-sm"
								autoFocus
							/>
						</Field>

						<Field
							label={t("allowed_origins")}
							hint={t("allowed_origins_hint")}
						>
							<Textarea
								value={origins}
								onChange={(e) => setOrigins(e.target.value)}
								placeholder={t("allowed_origins_example")}
								className="font-mono text-xs"
								rows={3}
							/>
						</Field>

						<div className="grid grid-cols-2 gap-3">
							<Field label={t("rate_limit")} hint={t("rate_limit_hint")}>
								<Input
									type="number"
									value={rateLimit}
									onChange={(e) => setRateLimit(e.target.value)}
									className="h-8 text-sm"
								/>
							</Field>
							<Field label={t("expiry")}>
								<Select value={expiryDays} onValueChange={setExpiryDays}>
									<SelectTrigger className="h-8 text-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="30">{t("days_30")}</SelectItem>
										<SelectItem value="90">{t("days_90")}</SelectItem>
										<SelectItem value="180">{t("days_180")}</SelectItem>
										<SelectItem value="365">{t("year_1")}</SelectItem>
									</SelectContent>
								</Select>
							</Field>
						</div>

						<Field label={t("scopes_label")}>
							<div className="space-y-2">
								{AVAILABLE_SCOPES.map((scope) => (
									<label
										key={scope.value}
										className="flex items-center gap-2 cursor-pointer"
									>
										<Checkbox
											checked={scopes.includes(scope.value)}
											onCheckedChange={(checked) => {
												setScopes((prev) =>
													checked
														? [...prev, scope.value]
														: prev.filter((s) => s !== scope.value),
												)
											}}
										/>
										<span className="text-sm">{t(scope.labelKey)}</span>
										<span className="text-[11px] text-muted-foreground font-mono">
											{scope.value}
										</span>
									</label>
								))}
							</div>
						</Field>

						<Field label={t("party_label")}>
							<PartyPicker
								value={partyId}
								onChange={(id) => setPartyId(id)}
								externalOnly={false}
								placeholder={t("party_placeholder")}
							/>
							{partyId && (
								<Button
									variant="ghost"
									size="sm"
									className="text-xs h-6 px-2 mt-1"
									onClick={() => setPartyId("")}
								>
									{t("clear_party")}
								</Button>
							)}
						</Field>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setShowCreate(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							size="sm"
							onClick={handleCreate}
							disabled={creating || !name.trim()}
						>
							{creating && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ── Plaintext reveal — no click-outside dismiss ── */}
			<Dialog open={!!plaintext} onOpenChange={(open) => { if (open) return; }}>
				<DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
					<DialogHeader>
						<DialogTitle>{t("key_created")}</DialogTitle>
						<DialogDescription>{t("key_created_desc")}</DialogDescription>
					</DialogHeader>
					<div className="bg-muted p-3 rounded-md font-mono text-sm break-all select-all">
						{plaintext}
					</div>
					<DialogFooter className="gap-2">
						<Button
							size="sm"
							onClick={() => {
								if (plaintext) copyToClipboard(plaintext);
								toast.success(t("copied"));
							}}
						>
							{copied ? (
								<Check className="h-3.5 w-3.5 mr-1.5" />
							) : (
								<Copy className="h-3.5 w-3.5 mr-1.5" />
							)}
							{t("copy")}
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => setPlaintext(null)}
						>
							{t("close")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* ── Revoke confirm ─────────────────────── */}
			<AlertDialog open={!!revokeId} onOpenChange={() => setRevokeId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("revoke_key_confirm")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("revoke_key_desc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleRevoke}
							className="bg-destructive text-destructive-foreground"
						>
							{t("revoke")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}

function Field({
	label,
	hint,
	children,
}: { label: string; hint?: string; children: React.ReactNode }) {
	return (
		<div className="space-y-1">
			<Label className="text-xs">{label}</Label>
			{children}
			{hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
		</div>
	)
}
